import {
  Control,
  Controller,
  FormState,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import { EditContactFormData } from './edit-personal-contact';
import { useEffect, useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import PersonalContactsEditAddressDialog from './personal-contacts-edit-address-dialog';
import PersonalContactsEditOrganizationDialog from './personal-contacts-edit-organization-dialog';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { darken, lighten, styled } from '@mui/material/styles';
import AddCircle from '@mui/icons-material/AddCircle';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface AccordionFormSectionProps {
  control: Control<EditContactFormData>;
  controlName:
    | 'emailAddresses'
    | 'phoneNumbers'
    | 'addresses'
    | 'relations'
    | 'organizations'
    | 'events'
    | 'imClients'
    | 'websites';
  title: string;
  selectPropertyOne: string;
  inputProperty: string;
  menuItemsOne: { label: string; value: string }[];
  formState: FormState<EditContactFormData>;
  setValue: UseFormSetValue<EditContactFormData>;
  watch: UseFormWatch<EditContactFormData>;
  getValues: UseFormGetValues<EditContactFormData>;
}

export default function AccordionFormSection({
  control,
  controlName,
  title,
  selectPropertyOne,
  menuItemsOne,
  inputProperty,
  formState,
  watch,
  setValue,
  getValues,
}: AccordionFormSectionProps) {
  const [open, setOpen] = useState(false);
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [openOrgsDialog, setOpenOrgsDialog] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(0);
  const { fields, append, remove } = useFieldArray({
    control,
    name: controlName,
  });

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (fields.length === 0) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [fields]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (formState.errors[controlName]) {
      setOpen(true);
    }
  }, [controlName, formState]);

  // scroll to errors in accordion
  useEffect(() => {
    if (!formState.errors) return;
    const firstErrorField = Object.keys(formState.errors)[0];
    if (firstErrorField) {
      const errorElement = document.querySelector(`[name=${firstErrorField}]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [formState]);

  const GroupHeader = styled('div')(({ theme }) => ({
    zIndex: 5,
    position: 'sticky',
    top: '-8px',
    padding: '4px 10px',
    color: theme.palette.primary.main,
    backgroundColor:
      theme.palette.mode === 'light'
        ? lighten(theme.palette.primary.light, 0.85)
        : darken(theme.palette.primary.main, 0.8),
  }));

  return (
    <>
      <Accordion
        expanded={open}
        onChange={() => {
          if (fields.length === 0) return;
          setOpen(!open);
        }}
      >
        <AccordionSummary
          expandIcon={
            <IconButton
              disabled={fields.length === 0}
              sx={{ mr: -1 }}
              disableRipple
            >
              <ExpandMore />
            </IconButton>
          }
          aria-controls={`${controlName}-content`}
          id={`${controlName}}-header`}
        >
          <Stack flexDirection="row" alignItems="center" width="100%" gap={1}>
            <Typography
              sx={{
                color: (theme) =>
                  formState.errors[controlName]
                    ? theme.palette.error.main
                    : 'inherit',
              }}
            >
              {title}
            </Typography>
            {controlName !== 'imClients' ? (
              <Tooltip title="Add" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (controlName === 'emailAddresses') {
                      append({
                        type: '',
                        value: '',
                      });
                    } else if (controlName === 'events') {
                      append({
                        type: '',
                        date: dayjs(),
                      });
                    } else if (controlName === 'websites') {
                      append({
                        type: '',
                        value: '',
                      });
                    } else if (controlName === 'phoneNumbers') {
                      append({
                        type: '',
                        value: '',
                      });
                    } else if (controlName === 'relations') {
                      append({ type: '', person: '' });
                    } else if (controlName === 'addresses') {
                      append({
                        type: '',
                        formattedValue: '',
                        poBox: '',
                        extendedAddress: '',
                        streetAddress: '',
                        city: '',
                        region: '',
                        postalCode: '',
                        country: '',
                        countryCode: '',
                      });
                    } else if (controlName === 'organizations') {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      append({
                        name: '',
                        title: '',
                        current: false,
                        type: '',
                        department: '',
                        symbol: '',
                        location: '',
                        jobDescription: '',
                        domain: '',
                      });
                    }
                    //   else {
                    //     append({
                    //       type: '',
                    //       primary: false,
                    //       customType: '',
                    //       value: '',
                    //     });
                    //   }
                    setOpen(true);
                  }}
                >
                  <AddCircle />
                </IconButton>
              </Tooltip>
            ) : null}
            {fields.length > 0 ? (
              <Chip label={fields.length} color="primary" size="small" />
            ) : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 140, overflowY: 'auto' }}>
          <Stack
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            {fields.map((item, index) => {
              const formattedAddress = watch(
                `addresses.${index}.formattedValue`
              );

              return (
                <Stack
                  gap={1}
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  key={item.id}
                >
                  <Stack flexDirection="row" alignItems="center" gap={2}>
                    {controlName !== 'events' && controlName !== 'imClients' ? (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={
                          `${controlName}.${index}.${selectPropertyOne}` as keyof EditContactFormData
                        }
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            inputRef={field.ref}
                            inputProps={{
                              id: `${controlName}-type-${index}`,
                            }}
                            InputLabelProps={{
                              htmlFor: `${controlName}-type-${index}`,
                            }}
                            name={controlName}
                            select
                            SelectProps={{
                              MenuProps: {
                                style: { maxHeight: 400 },
                              },
                            }}
                            label={`${selectPropertyOne}`}
                            error={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              !!formState.errors[controlName]?.[index]?.[
                                selectPropertyOne
                              ]
                            }
                            helperText={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              formState.errors[controlName]?.[index]?.[
                                selectPropertyOne
                              ]?.message || ' '
                            }
                            sx={{
                              // width: '100%',
                              width: 150,
                              // maxWidth: 500,
                              textTransform: 'capitalize',
                            }}
                          >
                            <GroupHeader>Current Values</GroupHeader>
                            {menuItemsOne.map((item) => {
                              if (
                                !item.value.includes('http://') &&
                                item.value.charAt(0) !==
                                  item.value.charAt(0).toUpperCase()
                              )
                                return (
                                  <MenuItem value={item.value} key={item.label}>
                                    {item.label}
                                  </MenuItem>
                                );
                            })}
                            <GroupHeader>Deprecated Values</GroupHeader>
                            {menuItemsOne.map((item) => {
                              if (
                                item.value.includes('http://') ||
                                item.value.charAt(0) ===
                                  item.value.charAt(0).toUpperCase()
                              )
                                return (
                                  <MenuItem value={item.value} key={item.label}>
                                    {item.label}
                                  </MenuItem>
                                );
                            })}
                          </TextField>
                        )}
                      />
                    ) : (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={
                          `${controlName}.${index}.${selectPropertyOne}` as keyof EditContactFormData
                        }
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            inputRef={field.ref}
                            inputProps={{
                              id: `${controlName}-type-${index}`,
                            }}
                            InputLabelProps={{
                              htmlFor: `${controlName}-type-${index}`,
                            }}
                            disabled={controlName === 'imClients'}
                            name={controlName}
                            select
                            SelectProps={{
                              MenuProps: {
                                style: { maxHeight: 400 },
                              },
                            }}
                            label={`${selectPropertyOne}`}
                            error={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              !!formState.errors[controlName]?.[index]?.[
                                selectPropertyOne
                              ]
                            }
                            helperText={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              formState.errors[controlName]?.[index]?.[
                                selectPropertyOne
                              ]?.message || ' '
                            }
                            sx={{
                              // width: '100%',
                              minWidth: 150,
                              // maxWidth: 500,
                              textTransform: 'capitalize',
                            }}
                          >
                            {menuItemsOne.map((item) => (
                              <MenuItem value={item.value} key={item.label}>
                                {item.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    )}
                    {controlName === 'addresses' ? (
                      <Typography
                        variant="caption"
                        sx={{
                          whiteSpace: 'pre-line',
                          mb: 3,
                        }}
                      >
                        {`${formattedAddress}`}
                      </Typography>
                    ) : controlName === 'organizations' ? (
                      <Typography
                        variant="caption"
                        sx={{ mb: 3, whiteSpace: 'pre-line' }}
                      >
                        {`${watch(`organizations.${index}.name`) || ''}`}
                      </Typography>
                    ) : controlName === 'events' ? (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={
                          `${controlName}.${index}.${inputProperty}` as keyof EditContactFormData
                        }
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <DatePicker
                            label="Date"
                            sx={{
                              minWidth: 250,
                            }}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            value={value ? value : null}
                            onChange={(e) => {
                              if (e) {
                                onChange(e);
                              }
                            }}
                            slotProps={{
                              textField: {
                                error:
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  !!formState.errors[controlName]?.[index]?.[
                                    inputProperty
                                  ],
                                helperText:
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  formState.errors[controlName]?.[index]?.[
                                    inputProperty
                                  ]?.message || ' ',
                              },
                            }}
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        rules={{ required: 'This field is required.' }}
                        name={
                          `${controlName}.${index}.${inputProperty}` as keyof EditContactFormData
                        }
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            required
                            inputRef={field.ref}
                            error={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              !!formState.errors[controlName]?.[index]?.[
                                inputProperty
                              ]
                            }
                            disabled={controlName === 'imClients'}
                            helperText={
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              formState.errors[controlName]?.[index]?.[
                                inputProperty
                              ]?.message || ' '
                            }
                            autoComplete="off"
                            label={`${inputProperty}`}
                            sx={{
                              minWidth: 250,
                              textTransform: 'capitalize',
                            }}
                          />
                        )}
                      />
                    )}

                    {controlName === 'organizations' ||
                    controlName === 'addresses' ? (
                      <Tooltip
                        title={`Edit ${
                          controlName === 'organizations'
                            ? 'Organization'
                            : 'Address'
                        }`}
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{ mb: 3 }}
                          onClick={() => {
                            setClickedIndex(index);

                            if (controlName === 'organizations') {
                              setOpenOrgsDialog(true);
                            } else {
                              setOpenAddressDialog(true);
                            }
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                  <Stack flexDirection="row" gap={2} sx={{ mb: 3 }}>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
      {openAddressDialog ? (
        <PersonalContactsEditAddressDialog
          openAddressDialog={openAddressDialog}
          setOpenAddressDialog={setOpenAddressDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={false}
          getValues={getValues}
        />
      ) : null}
      {openOrgsDialog ? (
        <PersonalContactsEditOrganizationDialog
          openOrganizationDialog={openOrgsDialog}
          setOpenOrganizationDialog={setOpenOrgsDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={false}
          getValues={getValues}
        />
      ) : null}
    </>
  );
}
